/**
 * Forgot Password Form Component - Account ID Version
 * Handles password reset with Account ID + Email verification
 */

import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Link
} from '@mui/material';
import { forgotUserPassword, confirmForgotUserPassword } from '../../services/auth/cognitoClient';

export default function ForgotPasswordForm({ onSuccess, onSignIn }) {
  const [step, setStep] = useState('request'); // 'request' or 'confirm'
  const [accountId, setAccountId] = useState('');
  const [email, setEmail] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Password validation
  const validatePassword = (pwd) => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  // Handle password reset request
  async function handleRequestReset(e) {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      // Remove spaces from Account ID
      const cleanAccountId = accountId.replace(/\s/g, '');
      await forgotUserPassword(cleanAccountId, email);
      setSuccessMessage('Password reset code sent! Please check your email.');
      setStep('confirm');
    } catch (err) {
      console.error('Forgot password error:', err);

      // User-friendly error messages
      if (err.message.includes('UserNotFoundException') || err.message.includes('not found')) {
        setError('No account found with this Account ID and email combination.');
      } else if (err.message.includes('LimitExceededException')) {
        setError('Too many attempts. Please try again later.');
      } else {
        setError(err.message || 'Failed to send reset code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  // Handle password reset confirmation
  async function handleConfirmReset(e) {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password requirements
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);

    try {
      // Remove spaces from Account ID
      const cleanAccountId = accountId.replace(/\s/g, '');
      await confirmForgotUserPassword(cleanAccountId, confirmationCode, newPassword);
      setSuccessMessage('Password reset successful! You can now sign in.');

      // Wait a moment to show success message, then trigger success callback
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(cleanAccountId);
        } else if (onSignIn) {
          onSignIn();
        }
      }, 1500);
    } catch (err) {
      console.error('Confirm reset error:', err);

      // User-friendly error messages
      if (err.name === 'CodeMismatchException' || err.message.includes('code')) {
        setError('Invalid verification code. Please check and try again.');
      } else if (err.name === 'ExpiredCodeException' || err.message.includes('expired')) {
        setError('Verification code has expired. Please request a new one.');
      } else if (err.name === 'InvalidPasswordException' || err.message.includes('password')) {
        setError('Password does not meet requirements.');
      } else {
        setError(err.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  // Render request reset form
  if (step === 'request') {
    return (
      <Box
        component="form"
        onSubmit={handleRequestReset}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 3
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom>
          Reset Password
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Enter your Account ID and email address to receive a verification code.
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success">
            {successMessage}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Account ID"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          disabled={loading}
          required
          placeholder="1234 5678 9012"
          helperText="12-digit Account ID (sent to your email when you signed up)"
          inputProps={{
            maxLength: 14,  // Allow spaces: "1234 5678 9012" (12 digits + 2 spaces)
            pattern: '[0-9 ]+',
            inputMode: 'numeric'
          }}
          autoFocus
        />

        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          required
          autoComplete="email"
        />

        <Button
          fullWidth
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ mt: 1, bgcolor: '#2d3e50', '&:hover': { bgcolor: '#3d4e60' } }}
        >
          {loading ? <CircularProgress size={24} /> : 'Send Reset Code'}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Remember your password?{' '}
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={onSignIn}
              disabled={loading}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
      </Box>
    );
  }

  // Render confirmation form
  return (
    <Box
      component="form"
      onSubmit={handleConfirmReset}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 3
      }}
    >
      <Typography variant="h5" component="h1" gutterBottom>
        Reset Password
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Enter the code we sent to <strong>{email}</strong> and your new password.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success">
          {successMessage}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Account ID"
        value={accountId}
        onChange={(e) => setAccountId(e.target.value)}
        disabled={loading}
        required
        placeholder="123456789012"
        helperText="Your 12-digit Account ID"
        inputProps={{
          maxLength: 13,
          pattern: '[0-9 ]+',
          inputMode: 'numeric'
        }}
      />

      <TextField
        fullWidth
        label="Verification Code"
        value={confirmationCode}
        onChange={(e) => setConfirmationCode(e.target.value)}
        disabled={loading}
        required
        autoFocus
        inputProps={{ maxLength: 6 }}
        helperText="Enter the 6-digit code from your email"
      />

      <TextField
        fullWidth
        label="New Password"
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        disabled={loading}
        required
        autoComplete="new-password"
        helperText="At least 8 characters with uppercase, lowercase, and number"
      />

      <TextField
        fullWidth
        label="Confirm New Password"
        type="password"
        value={confirmNewPassword}
        onChange={(e) => setConfirmNewPassword(e.target.value)}
        disabled={loading}
        required
        autoComplete="new-password"
      />

      <Button
        fullWidth
        type="submit"
        variant="contained"
        size="large"
        disabled={loading}
        sx={{ mt: 1, bgcolor: '#2d3e50', '&:hover': { bgcolor: '#3d4e60' } }}
      >
        {loading ? <CircularProgress size={24} /> : 'Reset Password'}
      </Button>

      <Box sx={{ textAlign: 'center', mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Didn't receive a code?{' '}
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setStep('request')}
            disabled={loading}
          >
            Resend Code
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
